import { useEffect, useState } from "react";
import { Form, FormInput, FormActions, FormTextArea, FormDate } from "../Controls/Form";
import TreeView from "../Controls/TreeView";
import SkillSearch from "../Controls/SkillSearch";
import CmModal from "../Controls/CmModal";
import API from "@src/API";
import { toast } from "react-toastify";

async function loadCategories() {
  const resp = await API.getAsync("/Skill/Categories");
  if (resp?.ok && Array.isArray(resp.jsonData)) {
    return resp.jsonData.map((node) => ({
      ID: node.ID ?? node.id,
      Parent: node.Parent ?? node.parent ?? 0,
      Name: node.Name ?? node.name ?? "",
    }));
  }
  return [];
}

function emptySkill(name = "") {
  return {
    ID: 0,
    ParentID: 0,
    ParentSkillName: "",
    Name: name || "",
    ShortName: "",
    CategoryID: 0,
    CategoryName: "",
    Description: "",
    ReleaseDate: null,
    Version: "",
    WikiUrl: "",
    Status: "pending",
    ReleaseFeedSlug: "",
  };
}

function normalizeSkill(raw, fallbackName = "") {
  if (!raw || typeof raw !== "object") return emptySkill(fallbackName);
  return {
    ...emptySkill(fallbackName),
    ID: raw.ID ?? raw.id ?? 0,
    ParentID: raw.ParentID ?? raw.parentID ?? 0,
    ParentSkillName: raw.ParentSkillName ?? raw.parentSkillName ?? "",
    Name: raw.Name ?? raw.name ?? fallbackName ?? "",
    ShortName: raw.ShortName ?? raw.shortName ?? "",
    CategoryID: raw.CategoryID ?? raw.categoryID ?? 0,
    CategoryName: raw.CategoryName ?? raw.categoryName ?? "",
    Description: raw.Description ?? raw.description ?? "",
    ReleaseDate: raw.ReleaseDate ?? raw.releaseDate ?? null,
    Version: raw.Version ?? raw.version ?? "",
    WikiUrl: raw.WikiUrl ?? raw.wikiUrl ?? "",
    Status: raw.Status ?? raw.status ?? "pending",
    ReleaseFeedSlug: raw.ReleaseFeedSlug ?? raw.releaseFeedSlug ?? "",
  };
}

function normalizeVersionRow(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    ID: raw.ID ?? raw.id ?? 0,
    SkillID: raw.SkillID ?? raw.skillID ?? 0,
    Version: raw.Version ?? raw.version ?? "",
    ReleaseDate: raw.ReleaseDate ?? raw.releaseDate ?? null,
    EndOfSupportDate: raw.EndOfSupportDate ?? raw.endOfSupportDate ?? null,
    IsMajor: !!(raw.IsMajor ?? raw.isMajor),
    IsLts: !!(raw.IsLts ?? raw.isLts),
    Status: raw.Status ?? raw.status ?? "current",
    SourceUrl: raw.SourceUrl ?? raw.sourceUrl ?? "",
    Description: raw.Description ?? raw.description ?? "",
  };
}

function emptyVersionDraft() {
  return {
    ID: 0,
    Version: "",
    ReleaseDate: null,
    EndOfSupportDate: null,
    IsMajor: true,
    IsLts: false,
    Status: "current",
    Description: "",
  };
}

function pick(obj, ...keys) {
  for (const k of keys) {
    if (obj?.[k] != null && obj[k] !== "") return obj[k];
  }
  return undefined;
}

function normalizeCandidate(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    Name: pick(raw, "Name", "name") || "",
    ShortName: pick(raw, "ShortName", "shortName") || "",
    Description: pick(raw, "Description", "description") || "",
    CategoryID: pick(raw, "CategoryID", "categoryID") || 0,
    CategoryName: pick(raw, "CategoryName", "categoryName") || "",
    WikiUrl: pick(raw, "WikiUrl", "wikiUrl") || "",
    ReleaseDate: pick(raw, "ReleaseDate", "releaseDate") || null,
    Confidence: Number(pick(raw, "Confidence", "confidence") ?? 0),
    MatchedSkillID: pick(raw, "MatchedSkillID", "matchedSkillID") || null,
    Rationale: pick(raw, "Rationale", "rationale") || "",
  };
}

function formatReleaseDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Prefer researched web definitions; ignore catalog-only hits. */
function pickBestProposal(candidates, query) {
  const list = (Array.isArray(candidates) ? candidates : [])
    .map(normalizeCandidate)
    .filter((c) => c && (c.Name || "").trim() && (c.Description || "").trim());
  if (!list.length) return null;

  const needle = (query || "").trim().toLowerCase();
  const exact = list.find(
    (c) =>
      c.Name.toLowerCase() === needle || (c.ShortName || "").toLowerCase() === needle
  );
  if (exact) return exact;

  return [...list].sort((a, b) => b.Confidence - a.Confidence)[0];
}

/**
 * Skill form body. Prefer SkillEditModal for UI entry points.
 */
export default function SkillEdit({
  skillId = 0,
  initialName = "",
  initialDraft = null,
  onClose,
  onHeaderChange,
}) {
  const id = Number.parseInt(skillId, 10) || 0;
  const isCreate = !(id > 0);
  const [busy, setBusy] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [fetchingReleases, setFetchingReleases] = useState(false);
  const [savingVersion, setSavingVersion] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [tab, setTab] = useState("info");
  const [versions, setVersions] = useState([]);
  const [versionDraft, setVersionDraft] = useState(() => emptyVersionDraft());
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [skill, updateSkill] = useState(() =>
    initialDraft
      ? normalizeSkill(initialDraft, initialName || initialDraft.Name || "")
      : emptySkill(initialName)
  );
  const [hydrated, setHydrated] = useState(isCreate);
  const skillIdValue = Number(skill.ID) || id || 0;

  useEffect(() => {
    if (!onHeaderChange) return;
    const skillIdValue = Number(skill.ID) || id || 0;
    const name = (skill.Name || "").trim() || (isCreate ? "New skill" : "Skill");
    onHeaderChange({ name, id: skillIdValue });
  }, [skill.ID, skill.Name, id, isCreate, onHeaderChange]);

  async function loadVersions(forSkillId) {
    if (!(forSkillId > 0)) {
      setVersions([]);
      return;
    }
    const resp = await API.getAsync(`/Skill/${forSkillId}/Versions`);
    if (resp?.ok && Array.isArray(resp.jsonData)) {
      setVersions(resp.jsonData.map(normalizeVersionRow).filter(Boolean));
    } else {
      setVersions([]);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setHydrated(isCreate);
    setVersions([]);
    setVersionDraft(emptyVersionDraft());
    setVersionModalOpen(false);
    if (!id) {
      if (initialDraft) {
        updateSkill(normalizeSkill(initialDraft, initialName || initialDraft.Name || ""));
      } else {
        updateSkill(emptySkill(initialName));
      }
      setHydrated(true);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      const resp = await API.getAsync("/Skill?ID=" + id);
      if (cancelled) return;
      if (resp?.ok && resp.jsonData) {
        const raw = Array.isArray(resp.jsonData) ? resp.jsonData[0] : resp.jsonData;
        const normalized = normalizeSkill(raw);
        updateSkill(normalized);
        const embedded = raw.Versions ?? raw.versions;
        if (Array.isArray(embedded) && embedded.length) {
          setVersions(embedded.map(normalizeVersionRow).filter(Boolean));
        } else {
          await loadVersions(normalized.ID || id);
        }
        setHydrated(true);
      } else {
        toast.error("Could not load skill");
        setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, initialName, initialDraft, isCreate]);

  function close(result) {
    onClose?.(result);
  }

  async function onLookupOnlineAsync() {
    const name = (skill.Name || "").trim();
    if (name.length < 2) {
      toast.error("Enter a skill name first");
      setTab("info");
      return;
    }
    if (lookingUp || busy) return;
    setLookingUp(true);
    setProposal(null);
    try {
      const resp = await API.postAsync("/Skill/Enrich", { Name: name });
      if (!resp?.ok) {
        toast.error(resp?.jsonData?.message || "Online lookup failed");
        return;
      }
      const data = resp.jsonData || {};
      const source = data.Source ?? data.source ?? "web";
      const best = pickBestProposal(data.Candidates ?? data.candidates, name);
      if (!best || !(best.Description || "").trim()) {
        toast.info("No useful online details found. Try a more specific name, or fill fields manually.");
        return;
      }
      setProposal({ ...best, _source: source });
      setTab("info");
      toast.success("Found online details — review and apply if they look right.");
    } catch {
      toast.error("Lookup failed");
    } finally {
      setLookingUp(false);
    }
  }

  function applyProposal() {
    if (!proposal) return;
    updateSkill((prev) => ({
      ...prev,
      Name: proposal.Name || prev.Name,
      ShortName: proposal.ShortName || prev.ShortName || proposal.Name || prev.Name,
      Description: proposal.Description || prev.Description,
      WikiUrl: proposal.WikiUrl || prev.WikiUrl,
      CategoryID: proposal.CategoryID || prev.CategoryID,
      CategoryName: proposal.CategoryName || prev.CategoryName,
      ReleaseDate: proposal.ReleaseDate || prev.ReleaseDate,
    }));
    setProposal(null);
    toast.success("Applied suggested details");
  }

  async function onDoneAsync() {
    if (busy) return;
    if (!(skill.Name || "").trim()) {
      toast.error("Skill name is required");
      setTab("info");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...skill,
        ID: skill.ID || id || 0,
        ParentID: skill.ParentID > 0 ? skill.ParentID : null,
        Name: (skill.Name || "").trim(),
        ShortName: (skill.ShortName || skill.Name || "").trim(),
      };
      const resp = await API.postAsync("/Skill", payload);
      if (resp?.ok) {
        toast.success("Skill saved");
        const raw = Array.isArray(resp.jsonData)
          ? resp.jsonData[resp.jsonData.length - 1]
          : resp.jsonData;
        const saved = normalizeSkill(raw, payload.Name);
        if (!saved.ID && raw) {
          saved.ID = raw.ID ?? raw.id ?? 0;
        }
        onClose?.(saved);
      } else {
        toast.error(resp?.jsonData?.message || "Save failed");
      }
    } finally {
      setBusy(false);
    }
  }

  function onParentSelect(selected) {
    if (!selected) return;
    const parentId = selected.ID ?? selected.id ?? 0;
    const name = selected.Name ?? selected.name ?? "";
    updateSkill((prev) => ({
      ...prev,
      ParentID: parentId,
      ParentSkillName: name,
    }));
  }

  async function onFetchReleasesAsync() {
    if (!(skillIdValue > 0)) {
      toast.error("Save the skill before fetching releases");
      return;
    }
    if (fetchingReleases || busy) return;
    setFetchingReleases(true);
    try {
      // Server uses Skill.ReleaseFeedSlug when body omits slug.
      const resp = await API.postAsync(`/Skill/${skillIdValue}/Versions/Fetch`, {});
      if (!resp?.ok) {
        toast.error(
          resp?.jsonData?.error ||
            resp?.jsonData?.Error ||
            "Fetch failed — this skill may not have a release feed configured"
        );
        return;
      }
      const data = resp.jsonData || {};
      const inserted = data.Inserted ?? data.inserted ?? 0;
      const updated = data.Updated ?? data.updated ?? 0;
      toast.success(`Fetched releases (${inserted} new, ${updated} updated)`);
      await loadVersions(skillIdValue);
    } catch {
      toast.error("Fetch failed");
    } finally {
      setFetchingReleases(false);
    }
  }

  function closeVersionModal() {
    setVersionModalOpen(false);
    setVersionDraft(emptyVersionDraft());
  }

  function openAddVersionModal() {
    if (!(skillIdValue > 0)) {
      toast.error("Save the skill before adding releases");
      return;
    }
    setVersionDraft(emptyVersionDraft());
    setVersionModalOpen(true);
  }

  function openEditVersionModal(row) {
    setVersionDraft({
      ID: row.ID || 0,
      Version: row.Version || "",
      ReleaseDate: row.ReleaseDate || null,
      EndOfSupportDate: row.EndOfSupportDate || null,
      IsMajor: !!row.IsMajor,
      IsLts: !!row.IsLts,
      Status: row.Status || "current",
      Description: row.Description || "",
    });
    setVersionModalOpen(true);
  }

  async function onSaveVersionAsync() {
    if (!(skillIdValue > 0)) {
      toast.error("Save the skill before adding releases");
      return;
    }
    const version = (versionDraft.Version || "").trim();
    if (!version) {
      toast.error("Version label is required");
      return;
    }
    if (savingVersion) return;
    setSavingVersion(true);
    try {
      const resp = await API.postAsync(`/Skill/${skillIdValue}/Versions`, {
        ...versionDraft,
        Version: version,
        SkillID: skillIdValue,
      });
      if (!resp?.ok) {
        toast.error(resp?.jsonData?.message || "Could not save release");
        return;
      }
      toast.success("Release saved");
      closeVersionModal();
      await loadVersions(skillIdValue);
    } finally {
      setSavingVersion(false);
    }
  }

  if (!hydrated) {
    return (
      <div className="cm-modal-embedded" data-testid="skill-edit-loading">
        <p className="fieldHint">Loading skill…</p>
      </div>
    );
  }

  return (
    <div className="cm-modal-embedded">
      <Form
        key={hydrated ? `skill-${id || "new"}-ready` : `skill-${id || "new"}-loading`}
        className="cm-modal-form"
        object={skill}
        onUpdate={updateSkill}
      >
        <div className="cm-modal-tabs" role="tablist" aria-label="Skill sections">
          <button
            type="button"
            role="tab"
            id="skill-tab-info"
            aria-selected={tab === "info"}
            aria-controls="skill-panel-info"
            className={tab === "info" ? "is-active" : undefined}
            data-testid="skill-tab-info"
            onClick={() => setTab("info")}
          >
            Info
          </button>
          <button
            type="button"
            role="tab"
            id="skill-tab-details"
            aria-selected={tab === "details"}
            aria-controls="skill-panel-details"
            className={tab === "details" ? "is-active" : undefined}
            data-testid="skill-tab-details"
            onClick={() => setTab("details")}
          >
            Details
          </button>
          <button
            type="button"
            role="tab"
            id="skill-tab-context"
            aria-selected={tab === "context"}
            aria-controls="skill-panel-context"
            className={tab === "context" ? "is-active" : undefined}
            data-testid="skill-tab-context"
            onClick={() => setTab("context")}
          >
            Context
          </button>
          <button
            type="button"
            role="tab"
            id="skill-tab-versions"
            aria-selected={tab === "versions"}
            aria-controls="skill-panel-versions"
            className={tab === "versions" ? "is-active" : undefined}
            data-testid="skill-tab-versions"
            onClick={() => setTab("versions")}
          >
            Versions{versions.length ? ` (${versions.length})` : ""}
          </button>
        </div>

        <div className="cm-modal-tab-panels">
          <div
            id="skill-panel-info"
            role="tabpanel"
            aria-labelledby="skill-tab-info"
            aria-hidden={tab !== "info"}
            className={`cm-modal-tab-panel${tab === "info" ? " is-active" : ""}`}
            {...(tab !== "info" ? { inert: "" } : {})}
          >
            {proposal ? (
              <div className="skill-edit-proposal" data-testid="skill-lookup-proposal">
                <div className="skill-edit-proposal__head">
                  <strong>Suggested details</strong>
                  {proposal.Confidence > 0 ? (
                    <span className="skill-edit-proposal__confidence">
                      {Math.round(Math.min(1, proposal.Confidence) * 100)}% match
                    </span>
                  ) : null}
                </div>
                <p className="skill-edit-proposal__name">{proposal.Name}</p>
                {proposal.Description ? (
                  <p className="skill-edit-proposal__desc">{proposal.Description}</p>
                ) : null}
                {proposal.CategoryName ? (
                  <p className="skill-edit-proposal__meta">Category: {proposal.CategoryName}</p>
                ) : null}
                {proposal.ReleaseDate ? (
                  <p className="skill-edit-proposal__meta">
                    Released: {formatReleaseDate(proposal.ReleaseDate)}
                  </p>
                ) : null}
                {proposal.WikiUrl ? (
                  <p className="skill-edit-proposal__meta">
                    Reference:{" "}
                    <a href={proposal.WikiUrl} target="_blank" rel="noreferrer">
                      {proposal.WikiUrl}
                    </a>
                  </p>
                ) : null}
                {proposal.Rationale ? (
                  <p className="skill-edit-proposal__rationale">{proposal.Rationale}</p>
                ) : null}
                {proposal._source ? (
                  <p className="skill-edit-proposal__meta">Source: {proposal._source}</p>
                ) : null}
                <div className="skill-edit-proposal__actions">
                  <button
                    type="button"
                    className="skill-edit-proposal__apply"
                    data-testid="skill-lookup-apply"
                    onClick={applyProposal}
                  >
                    Apply suggestions
                  </button>
                  <button
                    type="button"
                    className="skill-edit-proposal__dismiss"
                    onClick={() => setProposal(null)}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            ) : null}

            <FormInput
              field="Name"
              label={
                <>
                  Name
                  <span className="skill-edit-field-id" data-testid="skill-edit-field-id">
                    {(Number(skill.ID) || id) > 0
                      ? `(${Number(skill.ID) || id})`
                      : "(unsaved)"}
                  </span>
                </>
              }
            />
            <FormInput field="ShortName" label="Short Name" />
            <FormTextArea field="Description" label="Description" />
            <TreeView
              field="CategoryID"
              fieldDisplay="CategoryName"
              label="Category"
              getData={loadCategories}
              formObject={{
                object: skill,
                onChange: (value, field) => {
                  updateSkill((prev) => ({ ...prev, [field]: value }));
                },
              }}
            />
          </div>

          <div
            id="skill-panel-details"
            role="tabpanel"
            aria-labelledby="skill-tab-details"
            aria-hidden={tab !== "details"}
            className={`cm-modal-tab-panel${tab === "details" ? " is-active" : ""}`}
            {...(tab !== "details" ? { inert: "" } : {})}
          >
            <FormInput
              field="Version"
              label="Legacy version text"
              disabled
            />
            <p className="fieldHint">
              Prefer the Versions tab for published releases. This free-text field is legacy.
            </p>
            <FormDate field="ReleaseDate" label="Release Date" />
            <FormInput field="WikiUrl" label="Reference URL" />
            <FormInput field="Status" label="Status" disabled />
          </div>

          <div
            id="skill-panel-context"
            role="tabpanel"
            aria-labelledby="skill-tab-context"
            aria-hidden={tab !== "context"}
            className={`cm-modal-tab-panel${tab === "context" ? " is-active" : ""}`}
            {...(tab !== "context" ? { inert: "" } : {})}
          >
            <div className="field">
              <label className="form-label">Parent Skill</label>
              <SkillSearch
                object={skill}
                field="ParentID"
                fieldDisplay="ParentSkillName"
                allowCreate={false}
                onSelect={onParentSelect}
              />
            </div>
          </div>

          <div
            id="skill-panel-versions"
            role="tabpanel"
            aria-labelledby="skill-tab-versions"
            aria-hidden={tab !== "versions"}
            className={`cm-modal-tab-panel${tab === "versions" ? " is-active" : ""}`}
            {...(tab !== "versions" ? { inert: "" } : {})}
            data-testid="skill-panel-versions"
          >
            {!skillIdValue ? (
              <p className="fieldHint">Save the skill first to manage releases.</p>
            ) : (
              <>
                <div className="skill-edit-versions-toolbar">
                  <button
                    type="button"
                    className="skill-edit-lookup"
                    data-testid="skill-fetch-releases"
                    disabled={fetchingReleases || busy}
                    onClick={() => onFetchReleasesAsync()}
                  >
                    {fetchingReleases ? "Fetching…" : "Fetch releases"}
                  </button>
                  <button
                    type="button"
                    className="skill-edit-lookup"
                    data-testid="skill-add-version"
                    disabled={busy || savingVersion}
                    onClick={() => openAddVersionModal()}
                  >
                    Add Version
                  </button>
                </div>

                <div className="skill-edit-versions-list" data-testid="skill-versions-list">
                  {versions.length === 0 ? (
                    <p className="fieldHint">No releases yet.</p>
                  ) : (
                    <table className="skill-edit-versions-table">
                      <thead>
                        <tr>
                          <th>Version</th>
                          <th>Released</th>
                          <th>EOL</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {versions.map((v) => (
                          <tr
                            key={v.ID || v.Version}
                            className="skill-edit-versions-table__row"
                            tabIndex={0}
                            role="button"
                            data-testid={`skill-version-row-${v.ID || v.Version}`}
                            onClick={() => openEditVersionModal(v)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                openEditVersionModal(v);
                              }
                            }}
                          >
                            <td>
                              {v.Version}
                              {v.IsLts ? " (LTS)" : ""}
                            </td>
                            <td>{formatReleaseDate(v.ReleaseDate)}</td>
                            <td>{formatReleaseDate(v.EndOfSupportDate)}</td>
                            <td>{v.Status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <FormActions
          doneLabel={busy ? "Saving…" : "Save"}
          onSaveAsync={async () => {
            await onDoneAsync();
          }}
          onCancelAsync={async () => {
            close();
          }}
        >
          <button
            type="button"
            className="skill-edit-lookup"
            data-testid="skill-lookup-online"
            title="Research this skill online and propose a clearer name, description, and reference link"
            disabled={lookingUp || busy}
            onClick={() => onLookupOnlineAsync()}
          >
            {lookingUp ? "Looking up…" : "Look up online"}
          </button>
        </FormActions>
      </Form>

      <CmModal
        open={versionModalOpen}
        onClose={closeVersionModal}
        title={
          versionDraft.ID
            ? `Edit release${versionDraft.Version ? ` ${versionDraft.Version}` : ""}`
            : "Add release"
        }
        size="default"
        testId="skill-version-edit-modal"
        footer={
          <>
            <button type="button" disabled={savingVersion} onClick={closeVersionModal}>
              Cancel
            </button>
            <button
              type="button"
              data-testid="skill-version-save"
              disabled={savingVersion}
              onClick={() => onSaveVersionAsync()}
            >
              {savingVersion ? "Saving…" : versionDraft.ID ? "Update release" : "Add release"}
            </button>
          </>
        }
      >
        <div className="skill-edit-version-form" data-testid="skill-version-form">
          <div className="field">
            <label className="form-label">Version</label>
            <input
              type="text"
              value={versionDraft.Version}
              onChange={(e) =>
                setVersionDraft((prev) => ({ ...prev, Version: e.target.value }))
              }
              placeholder="e.g. 12"
            />
          </div>
          <div className="field">
            <label className="form-label">Status</label>
            <select
              value={versionDraft.Status}
              onChange={(e) =>
                setVersionDraft((prev) => ({ ...prev, Status: e.target.value }))
              }
            >
              <option value="preview">preview</option>
              <option value="current">current</option>
              <option value="maintenance">maintenance</option>
              <option value="eol">eol</option>
            </select>
          </div>
          <div className="field field--inline">
            <label>
              <input
                type="checkbox"
                checked={!!versionDraft.IsMajor}
                onChange={(e) =>
                  setVersionDraft((prev) => ({ ...prev, IsMajor: e.target.checked }))
                }
              />{" "}
              Major
            </label>
            <label>
              <input
                type="checkbox"
                checked={!!versionDraft.IsLts}
                onChange={(e) =>
                  setVersionDraft((prev) => ({ ...prev, IsLts: e.target.checked }))
                }
              />{" "}
              LTS
            </label>
          </div>
          <div className="field">
            <label className="form-label">Release date</label>
            <input
              type="date"
              value={
                versionDraft.ReleaseDate
                  ? String(versionDraft.ReleaseDate).slice(0, 10)
                  : ""
              }
              onChange={(e) =>
                setVersionDraft((prev) => ({
                  ...prev,
                  ReleaseDate: e.target.value || null,
                }))
              }
            />
          </div>
          <div className="field">
            <label className="form-label">End of support</label>
            <input
              type="date"
              value={
                versionDraft.EndOfSupportDate
                  ? String(versionDraft.EndOfSupportDate).slice(0, 10)
                  : ""
              }
              onChange={(e) =>
                setVersionDraft((prev) => ({
                  ...prev,
                  EndOfSupportDate: e.target.value || null,
                }))
              }
            />
          </div>
        </div>
      </CmModal>
    </div>
  );
}
